// ─────────────────────────────────────────────────────────────────────────────
// lib/ThemeContext.jsx
// Legge il tema attivo da Supabase (view_active_theme) e lo espone
// a tutta l'app tramite context. I componenti continuano a usare
// useThemeColors() senza cambiamenti.
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { makeColors, makeShadows, makeBase, DEFAULT_THEME } from './theme'

const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [brandColors, setBrandColors] = useState(() => makeColors())
  const [shadows, setShadows]         = useState(() => makeShadows())
  const [base, setBase]               = useState(() => makeBase())
  const [themeName, setThemeName]     = useState('Giro')
  const [loading, setLoading]         = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        // Solo in sviluppo: EXPO_PUBLIC_FORCE_THEME=Tour nel .env locale forza quel tema (per provare i colori del Tour o
        // della Vuelta senza cambiare il tema attivo per tutti gli utenti). Nelle build di produzione __DEV__ e' falso.
        const forced = __DEV__ ? process.env.EXPO_PUBLIC_FORCE_THEME : null
        const { data, error } = forced
          ? await supabase.from('dim_app_theme').select('*').eq('name', forced).single()
          : await supabase.from('view_active_theme').select('*').single()


        if (error || !data) throw error

        const c = makeColors(data.brand, data.brand_light, data.brand_dark, data.brand_text)
        
        
        setBrandColors(c)
        setShadows(makeShadows(data.brand))
        setBase(makeBase(c))
        setThemeName(data.name)
      } catch (e) {
        // Fallback silenzioso ai colori Giro di default
        console.warn('ThemeContext: fallback ai colori default', e?.message)
        setBrandColors(makeColors())
        setShadows(makeShadows())
        setBase(makeBase())
        setThemeName('Giro')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  return (
    <ThemeContext.Provider value={{ brandColors, shadows, base, themeName, loading }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useBrandColors() {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    // Fallback se usato fuori dal provider
    return { brandColors: makeColors(), shadows: makeShadows(), base: makeBase(), themeName: 'Giro' }
  }
  return ctx
}